<template>
  <div class="card p-4 shadow-sm">
    <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
      <div>
        <h5 class="mb-1">Employment, Tax & Exemptions</h5>
        <p class="text-muted small mb-0">Information for this employer-specific enrollment.</p>
      </div>
      <span v-if="employerName" class="badge bg-light text-dark border px-3 py-2">Employer: {{ employerName }}</span>
    </div>

    <div v-if="!enrollmentId" class="alert alert-warning mb-0">
      Select an employer before completing this section.
    </div>

    <form v-else @submit.prevent="save">
      <h6 class="mb-3">Employment</h6>
      <div class="row">
        <div class="col-lg-4 col-md-6 mb-3">
          <label class="form-label">Hire Date</label>
          <input v-model="form.hire_date" type="date" class="form-control" />
        </div>
        <div class="col-lg-4 col-md-6 mb-3">
          <label class="form-label">Hourly Wage</label>
          <input v-model="form.hourly_wage" type="number" min="0" step="0.01" class="form-control" />
        </div>
        <div class="col-lg-4 col-md-6 mb-3">
          <label class="form-label">Pay Frequency</label>
          <select v-model="form.pay_frequency" class="form-select">
            <option value="">Select</option>
            <option value="weekly">Weekly</option>
            <option value="bi-weekly">Bi-weekly</option>
            <option value="semi-monthly">Semi-monthly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        <div class="col-lg-4 col-md-6 mb-3">
          <label class="form-label">Overtime Rate</label>
          <input v-model="form.overtime_rate" type="number" min="0" step="0.01" class="form-control" />
        </div>
        <div class="col-lg-4 col-md-6 mb-3">
          <label class="form-label">Job Title</label>
          <input v-model="form.job_title" type="text" class="form-control" />
        </div>
        <div class="col-lg-4 col-md-6 mb-3">
          <label class="form-label">Work Location</label>
          <input v-model="form.work_location" type="text" class="form-control" />
        </div>
      </div>

      <hr />
      <h6 class="mb-3">Tax Withholding</h6>
      <div class="row">
        <div class="col-lg-4 col-md-6 mb-3">
          <label class="form-label">Federal Filing Status</label>
          <select v-model="form.fed_filing_status" class="form-select">
            <option value="">Select</option>
            <option value="single">Single or Married filing separately</option>
            <option value="married">Married filing jointly</option>
            <option value="married_higher_rate">Married, higher withholding rate</option>
            <option value="head_of_household">Head of household</option>
          </select>
        </div>
        <div class="col-lg-4 col-md-6 mb-3">
          <label class="form-label">State Filing Status</label>
          <input v-model="form.state_filing_status" type="text" class="form-control" />
        </div>
        <div class="col-lg-4 col-md-6 mb-3">
          <label class="form-label">Tax Allowances</label>
          <input v-model="form.tax_allowances" type="number" min="0" step="1" class="form-control" />
        </div>
        <div class="col-lg-6 col-md-6 mb-3">
          <label class="form-label">Additional Federal Withholding</label>
          <input v-model="form.additional_fed_withholding" type="number" min="0" step="0.01" class="form-control" />
        </div>
        <div class="col-lg-6 col-md-6 mb-3">
          <label class="form-label">Additional State Withholding</label>
          <input v-model="form.additional_state_withholding" type="number" min="0" step="0.01" class="form-control" />
        </div>
      </div>

      <hr />
      <h6 class="mb-3">Exemptions & Work Requirements</h6>
      <div class="row">
        <div class="col-lg-6 mb-3">
          <label class="form-label">Relationship to Employer</label>
          <input v-model="form.relationship_to_employer" type="text" class="form-control" placeholder="Example: unrelated employee, parent, spouse" />
        </div>
        <div class="col-lg-3 col-md-6 mb-3">
          <label class="form-label">Live-in Employee?</label>
          <select v-model="form.live_in_status" class="form-select">
            <option value="">Select</option>
            <option :value="true">Yes</option>
            <option :value="false">No</option>
          </select>
        </div>
        <div class="col-lg-3 col-md-6 mb-3">
          <label class="form-label">Driving Required?</label>
          <select v-model="form.driving_required" class="form-select">
            <option value="">Select</option>
            <option :value="true">Yes</option>
            <option :value="false">No</option>
          </select>
        </div>
      </div>

      <div v-if="form.driving_required === true" class="alert alert-info py-2">
        Auto Insurance and Driver's License uploads will be required in the Required Documents section.
      </div>

      <div class="text-end mt-3">
        <button class="btn bg-gradient-success text-white" type="submit" :disabled="saving">
          <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
          {{ saving ? 'Saving...' : 'Save Employment Details' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

const blankForm = () => ({
  hire_date: '',
  hourly_wage: '',
  pay_frequency: '',
  overtime_rate: '',
  job_title: '',
  work_location: '',
  fed_filing_status: '',
  state_filing_status: '',
  tax_allowances: '',
  additional_fed_withholding: '',
  additional_state_withholding: '',
  relationship_to_employer: '',
  live_in_status: '',
  driving_required: '',
});

export default {
  name: 'EnrollmentDetails',
  props: {
    enrollmentId: { type: [Number, String], default: null },
    clientId: { type: [Number, String], default: null },
    employerName: { type: String, default: '' },
    enrollmentData: { type: Object, default: null },
  },
  emits: ['enrollment-updated'],
  data() {
    return { form: blankForm(), saving: false };
  },
  watch: {
    enrollmentData: {
      immediate: true,
      deep: true,
      handler(data) {
        const e = data?.enrollment || {};
        const next = blankForm();
        Object.keys(next).forEach((key) => {
          if (e[key] !== undefined && e[key] !== null) next[key] = e[key];
        });
        this.form = next;
      },
    },
  },
  methods: {
    async save() {
      if (!this.enrollmentId || !this.clientId) return;
      this.saving = true;
      try {
        const payload = { ...this.form, client_id: Number(this.clientId) };
        const response = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/employee-enrollment/${this.enrollmentId}/update`,
          payload,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' } }
        );
        this.$emit('enrollment-updated', response.data?.data || null);
        Swal.fire({ icon: 'success', title: 'Saved', text: 'Employment details were updated.', timer: 1800, showConfirmButton: false });
      } catch (error) {
        const data = error.response?.data;
        const validation = data?.errors ? Object.values(data.errors).flat().join('\n') : null;
        Swal.fire('Save Failed', validation || data?.message || 'Could not save employment details.', 'error');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
